import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { markLeadLost, type Lead } from './crmApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

/**
 * "Yo'qotildi" — SABAB bilan.
 *
 * <h2>Nega modal kerak bo'ldi</h2>
 * Backend (`LeadServiceImpl.markLost`) sababsiz so'rovni rad etadi,
 * tugma esa uni umuman so'ramasdi va bo'sh tana yuborardi. Natijada
 * "Yo'qotildi" HAR BOSISHDA 400 qaytarardi — ya'ni holat umuman
 * qo'yib bo'lmasdi.
 *
 * <h2>⚠️ Tayyor sabablar + erkin matn — ikkalasi ham</h2>
 * Faqat erkin matn qoldirilsa har operator o'zicha yozadi
 * ("qimmat", "narx", "pul yo'q") va yo'qotish sabablari bo'yicha
 * hisobot chiqmaydi. Faqat ro'yxat qoldirilsa esa haqiqiy sabab
 * "Boshqa" ichida yo'qoladi. Shuning uchun ro'yxat tanlanadi va
 * izoh IXTIYORIY ravishda qo'shiladi.
 */
interface LeadLostModalProps {
  /** `null` — modal yopiq */
  lead: Lead | null
  onCancel: () => void
  onDone: () => void
}

/** Kalitlar — i18n uchun; serverga TARJIMA QILINGAN matn ketadi. */
const REASONS = ['PRICE', 'COMPETITOR', 'NO_COVERAGE', 'NO_ANSWER', 'NOT_INTERESTED', 'OTHER'] as const

interface LostValues {
  reasonCode: (typeof REASONS)[number]
  note?: string
}

export function LeadLostModal({ lead, onCancel, onDone }: LeadLostModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<LostValues>()

  useEffect(() => {
    if (lead) form.resetFields()
  }, [lead, form])

  const lost = useMutation({
    mutationFn: (reason: string) => markLeadLost(lead!.id, reason),
    onSuccess: onDone,
  })

  /**
   * Bekor qilib chiqish — xato xabari ham qolmasin: keyingi lid uchun u
   * eski va begona bo'lardi.
   *
   * ⚠️ Xato mutatsiyaning O'ZIDA turadi (`VoucherVoidModal` naqshi), alohida
   * `useState` da emas. Ilgari ikkinchi nusxa bor edi va uni qo'lda
   * tozalash kerak bo'lardi — bir joyda unutilsa ekranda begona xabar
   * qolardi.
   */
  const close = () => {
    lost.reset()
    onCancel()
  }

  return (
    <Modal
      open={lead !== null}
      title={t('crm.leadLost.title')}
      onCancel={close}
      onOk={() =>
        submitOnValid(form, (values) => {
          // ⚠️ Sabab MATN sifatida saqlanadi (`lead.lost_reason` — TEXT).
          // Kod emas, chunki baza uni ro'yxat sifatida bilmaydi; izoh
          // esa tanlangan sababdan KEYIN qo'shiladi, uni almashtirmaydi.
          const label = t(`crm.leadLost.reason.${values.reasonCode}`)
          const note = values.note?.trim()
          lost.mutate(note ? `${label} — ${note}` : label)
        })
      }
      confirmLoading={lost.isPending}
      okText={t('crm.leadLost.confirm')}
      okButtonProps={{ danger: true }}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {lead ? (
        <Typography.Paragraph type="secondary">
          {t('crm.leadLost.subtitle', { name: lead.fullName })}
        </Typography.Paragraph>
      ) : null}

      {lost.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(lost.error, t)}
        />
      ) : null}

      <Form<LostValues> form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="reasonCode"
          label={t('crm.leadLost.reasonLabel')}
          rules={[{ required: true, message: t('crm.leadLost.reasonRequired') }]}
        >
          <Select
            autoFocus
            options={REASONS.map((code) => ({
              value: code,
              label: t(`crm.leadLost.reason.${code}`),
            }))}
          />
        </Form.Item>
        <Form.Item name="note" label={t('crm.leadLost.note')} extra={t('crm.leadLost.noteHint')}>
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
